import { formatDecimal, parseDecimalString } from "@compliance/domain";
import { Badge, Group, Text } from "@mantine/core";
import type { Figure, FigureBasis } from "../api/types";

type SupportedBasis = Extract<
  FigureBasis,
  | "HHV"
  | "LHV"
  | "MJ"
  | "scf"
  | "mmbtu"
  | "rin_d3"
  | "rf_gallon"
  | "therm"
  | "kwh"
  | "lbs"
  | "gCO2e_per_MJ"
  | "unitless_fraction"
>;

type Props =
  | {
      /** Pass a Figure object directly (the API-shaped form). */
      figure: Figure<SupportedBasis>;
      decimalPlaces?: number;
      showBasis?: boolean;
    }
  | {
      /** Or pass value+basis separately. */
      value: string;
      basis: SupportedBasis;
      decimalPlaces?: number;
      showBasis?: boolean;
    };

const BASIS_LABEL: Record<SupportedBasis, string> = {
  HHV: "MMBtu HHV",
  LHV: "MMBtu LHV",
  MJ: "MJ",
  scf: "SCF",
  mmbtu: "MMBtu",
  rin_d3: "D3 RIN",
  rf_gallon: "gal",
  therm: "therms",
  kwh: "kWh",
  lbs: "lbs",
  gCO2e_per_MJ: "g CO₂e/MJ",
  unitless_fraction: "",
};

/**
 * Renders a compliance figure with its basis badge. Never converts; only
 * parses the backend's canonical decimal string and formats it via decimal.js.
 */
export function UnitValue(props: Props) {
  const { value, basis } = "figure" in props ? props.figure : props;
  const { decimalPlaces, showBasis = true } = props;

  let formatted: string;
  try {
    formatted = formatDecimal(parseDecimalString(value), { decimalPlaces });
  } catch {
    return (
      <Group gap={4} wrap="nowrap">
        <Text c="red" fw={600}>
          [?] data error
        </Text>
      </Group>
    );
  }

  const basisLabel = BASIS_LABEL[basis];

  return (
    <Group gap={6} wrap="nowrap">
      <Text fw={500} ff="monospace">
        {formatted}
      </Text>
      {showBasis && basisLabel ? (
        <Badge variant="default" size="xs" radius="sm">
          {basisLabel}
        </Badge>
      ) : null}
    </Group>
  );
}
